import React from 'react'
import { useNavigate } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import Spinner, { SpinnerSize } from '@divvy-web/skylab.spinner'
import { TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import MarketingContent, { MarketingQuote } from '@sputnik/components/MarketingContent'
import PollingPageContent from '@sputnik/components/PollingPageContent/PollingPageContent'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import AfterSubmitInfoPageLayout from '@sputnik/pages/PostApplicationPages/AfterSubmitInfoPageLayout'
import { PATHNAME_CONSTS, URL } from '@sputnik/resources/constants'

import styles from './styles.module.scss'

export interface AcceptingOfferPollingPageContentProps {
  isReadyToNavigate: boolean
  stopPolling: () => void
  salesforceCreditId: string
  invitationToken?: string
}
const AcceptingOfferPollingPageContent = ({
  isReadyToNavigate,
  invitationToken,
  stopPolling,
  salesforceCreditId,
}: AcceptingOfferPollingPageContentProps) => {
  const navigate = useNavigate()
  const showSuccessToast = useToast(TOAST_TYPE_SUCCESS) as any
  const { formatMessage } = useIntl()
  const [getClassName] = useNameFormatter('AcceptingOfferPollingPageContent')
  const { isAppVersion3 } = useGetCreditAppTypes({ salesforceCreditId })
  const navigateToNextPath = () => {
    if (isAppVersion3 && invitationToken) {
      window.open(`${URL.DIVVY_PROD_PASSWORD_SETUP}/${invitationToken}`, '_blank', 'noopener, noreferrer')
    }

    navigate(PATHNAME_CONSTS.DASHBOARD_PATH, { replace: true })
  }

  if (isReadyToNavigate) {
    stopPolling()
    navigateToNextPath()
    showSuccessToast(
      <FormattedMessage
        defaultMessage='Offer accepted'
        id='sputnik.AcceptingOfferPollingPageContent__IB0Vm6'
      />,
      {
        autoHideDelay: 5000,
      },
    )
  }

  if (isAppVersion3) {
    return (
      <AfterSubmitInfoPageLayout
        imageName='post-application-info-page'
        infoContent={
          <>
            <FormattedMessage
              defaultMessage='This can take a minute or two. Please do not close this page.'
              id='sputnik.AcceptingOfferPollingPageContent__WJQRQZ'
            />
            <div className={styles[getClassName('spinner-and-text')]}>
              <Spinner size={SpinnerSize.Small} />
              <FormattedMessage
                defaultMessage='This can take up to a minute'
                id='sputnik.AcceptingOfferPollingPageContent__72Pvw0'
              />
            </div>
          </>
        }
        marketing={
          <MarketingContent
            image={{
              alt: formatMessage({
                defaultMessage: 'woman at a desk with a laptop checking her bill spend and expense account',
                id: 'sputnik.AcceptingOfferPollingPageContent__4VFaNh',
              }),
              src: 'https://app.divvy.co/assets/stock-photos/credit-application-offer-acceptance.png',
              isRounded: true,
            }}
            pillText={
              <FormattedMessage
                defaultMessage='Testimonial'
                id='sputnik.AcceptingOfferPollingPageContent__MvBefC'
              />
            }
          >
            <MarketingQuote
              designation={
                <FormattedMessage
                  defaultMessage='Accounting Manager, Chris Farmand'
                  id='sputnik.AcceptingOfferPollingPageContent__sc4yUT'
                />
              }
              name={
                <FormattedMessage
                  defaultMessage='Jonah Ebenhoeh'
                  id='sputnik.AcceptingOfferPollingPageContent__ttWQnr'
                />
              }
              quote={
                <FormattedMessage
                  defaultMessage='“The flexibility Spend & Expense has to empower the client to spend as they need but then how easy it is for them to come back to it and code to exactly what budget it goes to <strong>just makes life easier</strong>.”'
                  id='sputnik.AcceptingOfferPollingPageContent__6gjeO/'
                  values={{
                    strong: (chunks) => <b>{chunks}</b>,
                  }}
                />
              }
            />
          </MarketingContent>
        }
        title={
          <FormattedMessage
            defaultMessage="Hang tight! We're processing your acceptance"
            id='sputnik.AcceptingOfferPollingPageContent__ouKCvL'
          />
        }
      />
    )
  } else {
    // TODO: remove this when v2 apps are no longer supported
    return (
      <PollingPageContent
        mainText={
          <FormattedMessage
            defaultMessage="Hang tight! We're processing your acceptance."
            id='sputnik.AcceptingOfferPollingPageContent__TnYOAx'
          />
        }
        subText={
          <FormattedMessage
            defaultMessage='This can take a minute or two. Please do not close this page.'
            id='sputnik.AcceptingOfferPollingPageContent__WJQRQZ'
          />
        }
      />
    )
  }
}

export default AcceptingOfferPollingPageContent
